import * as React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Button,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Container,
  Link,
  InputAdornment,
  IconButton
} from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { newStudent, createStudentPayload, Student } from "../../models/student";
import { useSnackbar } from "../../components/snackbar";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

export default function SignIn() {
    const [student, setStudent] = useState<Student>(newStudent());
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const { showMessage } = useSnackbar();

    const handleStudentChange = <K extends keyof Student>(field: K, value: Student[K]) => {
        setStudent((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async () => {
        if (!student.email || !student.password) {
            showMessage('fill in all fields', 1);
            return;            
        }

        try {
            setLoading(true);
            await axios.post(`${API_URL}/auth/students/sign-in`, 
                {
                    email: student.email,
                    password: student.password
                },
                { withCredentials: true }
            );
            // navigate(`/verify/sign-in?email=${student.email}`);
            window.location.href = '/';
        } catch (error: any) {
            showMessage(error.response?.data?.detail || 'an error occurred', 1);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex" }}>
            {/* Right Side - Sign In Form */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "grey.100",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        p: 3,
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.5rem",
                            userSelect: "none",
                            fontFamily: "'Inter', 'Roboto', sans-serif",
                            color: "#3B82F6",
                            letterSpacing: "-0.02em"
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 800,
                                fontSize: "1.6rem"
                            }}
                        >
                            e
                        </Box>
                        LMS
                        <Box
                            component="span"
                            sx={{
                                color: "green",
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                ml: 1,
                                opacity: 0.7
                            }}
                        >
                            Student
                        </Box>
                    </Typography>
                </Box>

                {/* Form Container */}
                <Container
                    maxWidth="sm"
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        py: 4
                    }}
                >
                    <Box sx={{ maxWidth: 400, mx: "auto", width: "100%" }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                mb: 1,
                                color: "#1a1a1a"
                            }}
                        >
                            Sign in
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 4,
                                color: "text.secondary"
                            }}
                        >
                            Welcome back! Please enter your details.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Email"
                            variant="outlined"
                            value={student.email}
                            onChange={(e) => handleStudentChange("email", e.target.value)}
                            sx={{
                                mb: 3,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    '&:hover fieldset': {
                                        borderColor: '#667eea'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea'
                                    }
                                }
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Password"
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            value={student.password}
                            onChange={(e) => handleStudentChange("password", e.target.value)}
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    borderRadius: 3,
                                    '&:hover fieldset': {
                                        borderColor: '#667eea'
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea'
                                    }
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: "right", mb: 3 }}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => navigate('/reset-password')}
                                sx={{
                                    color: '#667eea',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleSubmit}
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                borderRadius: 3,
                                backgroundColor: '#3B82F6',
                                boxShadow: "0 4px 20px rgba(102, 126, 234, 0.4)",
                                fontSize: "1rem",
                                fontWeight: 600,
                                textTransform: "none",
                                '&:hover': {
                                    boxShadow: "0 6px 25px rgba(102, 126, 234, 0.6)",
                                    transform: "translateY(-1px)"
                                },
                                transition: "all 0.3s ease"
                            }}
                        >
                            Sign in
                        </Button>

                        <Typography variant="body2" color="text.secondary" textAlign="center" marginTop={2}>
                            Don't have an account?{' '}
                            <Link 
                                component="button"
                                onClick={() => navigate('/sign-up')}
                                sx={{ 
                                    color: '#667eea',
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                Sign up
                            </Link>
                        </Typography>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}